// Statute periods, per state, for the firm to confirm or change.
//
// The app knew five states and returned nothing for the other
// forty-five, so a matter arising elsewhere produced no statute date —
// which on the SOL card reads as "not computable yet" rather than
// "this app does not know". All fifty plus D.C. are here now.
//
// A DEFAULT IS NOT AN ANSWER. Every state starts unconfirmed and says
// so. The periods are used either way, because a documented default
// beats no date at all, but nothing here pretends the firm has checked
// something it has not. Statutes move — Florida went from four years
// to two in 2023, Louisiana from one to two in 2024 — so the table
// carries the date it was captured, and confirming a state is how a
// firm records that somebody looked.
//
// Several states cannot be described by one number: Colorado is 2
// years or 3 with a motor vehicle, Kentucky 1 or 2. Those carry a
// caveat shown next to the period. The app computes the base and tells
// somebody the exception exists rather than guessing which applies.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

export function SolRulesSection({ showNotification }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [onlyUnconfirmed, setOnlyUnconfirmed] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await apiFetch("/api/firm/sol-rules"));
    } catch (e) {
      showNotification && showNotification(
        "Could not load statute periods: " + e.message, "error");
      setData({ rules: [], total: 0, confirmed: 0 });
    }
  }, [showNotification]);

  useEffect(() => { load(); }, [load]);

  const save = async (state, patch) => {
    setBusy(state);
    try {
      await apiFetch("/api/firm/sol-rules", {
        method: "PATCH",
        body: JSON.stringify({ state, ...patch }),
      });
      await load();
    } catch (e) {
      showNotification && showNotification(e.message, "error");
    }
    setBusy(null);
  };

  if (data === null) {
    return React.createElement("div", {
      style: { display: "flex", gap: "8px", alignItems: "center", padding: "20px 0" },
    },
      React.createElement(Spinner, { size: 14 }),
      React.createElement("span", { style: { fontSize: "13px", color: C.textMuted } },
        "Loading statute periods…"));
  }

  const shown = onlyUnconfirmed
    ? data.rules.filter((r) => !r.is_confirmed) : data.rules;

  return React.createElement("div", { style: { marginTop: "34px" } },
    React.createElement("h2", {
      style: { fontFamily: "'Playfair Display',serif", fontSize: "20px",
               color: C.navy, marginBottom: "4px" },
    }, "Statute of limitations by state"),
    React.createElement("p", {
      style: { fontSize: "13.5px", color: C.textMuted, lineHeight: 1.6,
               marginBottom: "12px", maxWidth: "680px" },
    }, "The base personal-injury period used to compute a statute date. "
     + "These are starting points, not verified law: statutes change, "
     + "and confirming a state records that somebody has checked it "
     + `against current law. Captured ${data.defaults_as_of || "recently"}.`),

    React.createElement("div", {
      className: "wrap-row",
      style: { display: "flex", alignItems: "center", gap: "12px",
               marginBottom: "14px", flexWrap: "wrap" },
    },
      React.createElement("span", {
        style: { fontSize: "13px", fontWeight: 600, color: C.textDark },
      }, `${data.confirmed} of ${data.total} states confirmed`),
      React.createElement("button", {
        onClick: () => setOnlyUnconfirmed((v) => !v),
        style: quiet(onlyUnconfirmed),
      }, onlyUnconfirmed ? "Showing unconfirmed" : "Show unconfirmed only"),
      React.createElement("button", {
        onClick: () => setOpen((v) => !v), style: quiet(false),
      }, open ? "Hide states" : "Edit states"),
    ),

    open && React.createElement("div", {
      style: { border: `1px solid ${C.warmGray}`, borderRadius: "10px",
               background: "#fff", overflow: "hidden", maxHeight: "520px",
               overflowY: "auto" },
    },
      ...shown.map((r, i) => React.createElement(StateRow, {
        key: r.state, rule: r, first: i === 0,
        busy: busy === r.state, onSave: save,
      })),
      shown.length === 0 && React.createElement("div", {
        style: { padding: "20px", fontSize: "13px", color: C.textMuted },
      }, "Every state has been confirmed."),
    ),
  );
}


function StateRow({ rule, first, busy, onSave }) {
  const [years, setYears] = useState(String(rule.years ?? ""));
  const dirty = years !== String(rule.years ?? "");

  return React.createElement("div", {
    className: "stack-row",
    style: {
      display: "flex", gap: "12px", alignItems: "center", padding: "10px 15px",
      borderTop: first ? "none" : `1px solid ${C.warmGray}55`,
    },
  },
    React.createElement("div", { style: { flex: 1, minWidth: 0 } },
      React.createElement("div", {
        style: { fontSize: "13px", color: C.textDark, fontWeight: 600 },
      }, rule.state_name,
        rule.is_firm_override && React.createElement("span", {
          style: { fontSize: "11px", color: C.textMuted, fontWeight: 400,
                   marginLeft: "6px" },
        }, "· your own figure"),
      ),
      React.createElement("div", {
        style: { fontSize: "11px", color: C.textLight },
      }, rule.citation),
      // Shown, never applied. The app does not know whether a motor
      // vehicle was involved, so it states the exception instead of
      // choosing one.
      rule.caveat && React.createElement("div", {
        style: { fontSize: "11.5px", color: C.textMuted, marginTop: "3px",
                 lineHeight: 1.45 },
      }, rule.caveat),
    ),

    React.createElement("input", {
      type: "number", min: 1, max: 20, value: years,
      onChange: (e) => setYears(e.target.value),
      style: { width: "62px", padding: "6px 8px", fontSize: "13px",
               border: `1px solid ${C.warmGray}`, borderRadius: "6px",
               textAlign: "right", flexShrink: 0 },
    }),
    React.createElement("span", {
      style: { fontSize: "11px", color: C.textMuted, flexShrink: 0 },
    }, "years"),

    dirty
      ? React.createElement("button", {
          onClick: () => onSave(rule.state, { years: Number(years) }),
          disabled: busy, style: primary,
        }, busy ? "…" : "Save")
      : React.createElement("button", {
          onClick: () => onSave(rule.state, { confirmed: !rule.is_confirmed }),
          disabled: busy,
          style: rule.is_confirmed ? confirmed : quiet(false),
        }, busy ? "…" : (rule.is_confirmed ? "Confirmed" : "Confirm")),
  );
}

const quiet = (on) => ({
  border: `1px solid ${on ? C.navy : C.warmGray}`,
  background: on ? C.navy : "#fff", color: on ? "#fff" : C.navy,
  borderRadius: "6px", padding: "6px 12px", fontSize: "12px",
  fontWeight: 600, cursor: "pointer", flexShrink: 0,
});
const primary = {
  border: "none", background: C.navy, color: "#fff", borderRadius: "6px",
  padding: "6px 14px", fontSize: "12px", fontWeight: 600,
  cursor: "pointer", flexShrink: 0,
};
// Confirmed is stated, not coloured in — it is a fact about who has
// checked something, not a severity.
const confirmed = {
  border: `1px solid ${C.warmGray}`, background: C.warmWhite,
  color: C.textMuted, borderRadius: "6px", padding: "6px 12px",
  fontSize: "12px", fontWeight: 600, cursor: "pointer", flexShrink: 0,
};
